import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Card,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material'
import AddBoxIcon from '@mui/icons-material/AddBox'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import ImageIcon from '@mui/icons-material/Image'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import TimerIcon from '@mui/icons-material/Timer'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import type { IBlock, BlockType, BlockPayload } from '../../types'
import { BLOCK_TYPE_META } from '../../types'
import { getDefaultPayload } from './defaultPayloads'
import { BlockPayloadForm } from './BlockPayloadForm'

const BLOCK_TYPES: BlockType[] = [
  'IMAGE',
  'VIDEO',
  'CONTENT',
  'SLIDER',
  'FORM',
  'COUNTDOWN',
  'BANK_ACCOUNTS',
]

const BLOCK_ICONS: Record<BlockType, React.ReactNode> = {
  IMAGE: <ImageIcon sx={{ fontSize: 18 }} />,
  VIDEO: <VideoLibraryIcon sx={{ fontSize: 18 }} />,
  CONTENT: <TextFieldsIcon sx={{ fontSize: 18 }} />,
  SLIDER: <ViewCarouselIcon sx={{ fontSize: 18 }} />,
  FORM: <ShoppingCartIcon sx={{ fontSize: 18 }} />,
  COUNTDOWN: <TimerIcon sx={{ fontSize: 18 }} />,
  BANK_ACCOUNTS: <AccountBalanceIcon sx={{ fontSize: 18 }} />,
}

interface BlockEditorProps {
  value?: IBlock[]
  onChange?: (blocks: IBlock[]) => void
  disabled?: boolean
}

const normalizeOrders = (blocks: IBlock[]): IBlock[] =>
  blocks
    .map((b, i) => ({ ...b, order: i }))
    .sort((a, b) => a.order - b.order)

/* ── Sortable block item ── */

interface SortableBlockItemProps {
  id: string
  index: number
  block: IBlock
  blocksLength: number
  expanded: boolean
  disabled?: boolean
  onToggleExpand: (index: number) => void
  onMoveBlock: (index: number, dir: 'up' | 'down') => void
  onCloneBlock: (index: number) => void
  onRemoveBlock: (index: number) => void
  onUpdateBlock: (index: number, updates: Partial<IBlock>) => void
}

const SortableBlockItem: React.FC<SortableBlockItemProps> = ({
  id,
  index,
  block,
  blocksLength,
  expanded,
  disabled,
  onToggleExpand,
  onMoveBlock,
  onCloneBlock,
  onRemoveBlock,
  onUpdateBlock,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled })

  const meta = BLOCK_TYPE_META[block.type]

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
    position: 'relative' as const,
  }

  return (
    <Card
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{
        mb: 1.5,
        overflow: 'hidden',
        borderRadius: 2,
        borderColor: expanded ? meta.color : 'divider',
        borderWidth: expanded ? 1.5 : 1,
        transition: 'border-color 0.2s',
        '&:hover': { borderColor: meta.color },
      }}
    >
      {/* Block header */}
      <Box
        onClick={() => !disabled && onToggleExpand(index)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: 1,
          cursor: disabled ? 'default' : 'pointer',
          background: `linear-gradient(90deg, ${meta.color}14 0%, transparent 60%)`,
          borderBottom: expanded ? 1 : 0,
          borderColor: 'divider',
        }}
      >
        {/* Drag handle */}
        <Box
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: disabled ? 'default' : 'grab',
            touchAction: 'none',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicatorIcon
            sx={{ color: 'text.disabled', fontSize: 20 }}
          />
        </Box>

        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            bgcolor: `${meta.color}22`,
            color: meta.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {BLOCK_ICONS[block.type]}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600}>
            {meta.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Block #{index + 1}
          </Typography>
        </Box>
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{ display: 'flex', alignItems: 'center', gap: 0 }}
        >
          <IconButton
            size="small"
            disabled={index === 0 || disabled}
            onClick={() => onMoveBlock(index, 'up')}
            title="เลื่อนขึ้น"
          >
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            disabled={index === blocksLength - 1 || disabled}
            onClick={() => onMoveBlock(index, 'down')}
            title="เลื่อนลง"
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
          <Tooltip title="Clone Block">
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => onCloneBlock(index)}
              sx={{
                '&:hover': {
                  bgcolor: 'rgba(25, 118, 210, 0.08)',
                  color: 'primary.main',
                },
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            color="error"
            disabled={disabled}
            onClick={() => onRemoveBlock(index)}
            title="ลบ Block"
            sx={{
              '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.08)' },
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
        <ExpandMoreIcon
          sx={{
            color: 'text.secondary',
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
      </Box>

      {/* Block body */}
      {expanded && (
        <Box sx={{ p: 2 }}>
          <BlockPayloadForm
            block={block}
            onChange={(payload: BlockPayload) =>
              onUpdateBlock(index, { payload })
            }
          />
        </Box>
      )}
    </Card>
  )
}

/* ── Main editor ── */

export const BlockEditor: React.FC<BlockEditorProps> = ({
  value = [],
  onChange,
  disabled,
}) => {
  const blocks = [...value].sort((a, b) => a.order - b.order)

  const blockIds = useMemo(
    () => blocks.map((_, i) => `block-${i}`),
    [blocks.length],
  )

  const [expandedBlocks, setExpandedBlocks] = useState<Record<number, boolean>>(
    () =>
      blocks.reduce<Record<number, boolean>>((acc, _, i) => {
        acc[i] = true
        return acc
      }, {}),
  )

  useEffect(() => {
    setExpandedBlocks((prev) => {
      const next: Record<number, boolean> = {}
      blocks.forEach((_, i) => {
        next[i] = prev[i] ?? true
      })
      return next
    })
  }, [blocks.length])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = blockIds.indexOf(active.id as string)
    const newIndex = blockIds.indexOf(over.id as string)
    if (oldIndex === -1 || newIndex === -1) return

    const next = [...blocks]
    const [moved] = next.splice(oldIndex, 1)
    next.splice(newIndex, 0, moved)

    setExpandedBlocks((prev) => {
      const entries = Object.entries(prev).sort(
        ([a], [b]) => Number(a) - Number(b),
      )
      const values = entries.map(([, v]) => v)
      const [movedVal] = values.splice(oldIndex, 1)
      values.splice(newIndex, 0, movedVal)
      const out: Record<number, boolean> = {}
      values.forEach((v, i) => {
        out[i] = v
      })
      return out
    })

    onChange?.(normalizeOrders(next))
  }

  const addBlock = (type: BlockType) => {
    const newBlock: IBlock = {
      type,
      order: blocks.length,
      payload: getDefaultPayload(type),
      settings: {},
    }
    const next = normalizeOrders([...blocks, newBlock])
    setExpandedBlocks((prev) => ({ ...prev, [next.length - 1]: true }))
    onChange?.(next)
  }

  const updateBlock = (index: number, updates: Partial<IBlock>) => {
    const next = blocks.map((b, i) =>
      i === index ? { ...b, ...updates } : b,
    )
    onChange?.(normalizeOrders(next))
  }

  const removeBlock = (index: number) => {
    const next = blocks.filter((_, i) => i !== index)
    setExpandedBlocks((prev) => {
      const out: Record<number, boolean> = {}
      Object.entries(prev)
        .filter(([k]) => Number(k) !== index)
        .forEach(([k, v]) => {
          const n = Number(k)
          out[n > index ? n - 1 : n] = v
        })
      return out
    })
    onChange?.(normalizeOrders(next))
  }

  const cloneBlock = (index: number) => {
    const source = blocks[index]
    const cloned: IBlock = {
      type: source.type,
      order: index + 1,
      payload: JSON.parse(JSON.stringify(source.payload)),
      settings: source.settings ? JSON.parse(JSON.stringify(source.settings)) : {},
    }
    const next = [...blocks]
    next.splice(index + 1, 0, cloned)
    const normalized = normalizeOrders(next)
    setExpandedBlocks((prev) => {
      const out: Record<number, boolean> = {}
      Object.entries(prev).forEach(([k, v]) => {
        const n = Number(k)
        out[n >= index + 1 ? n + 1 : n] = v
      })
      out[index + 1] = true
      return out
    })
    onChange?.(normalized)
  }

  const moveBlock = (index: number, direction: 'up' | 'down') => {
    const target = direction === 'up' ? index - 1 : index + 1
    if (target < 0 || target >= blocks.length) return
    const next = [...blocks]
    ;[next[index], next[target]] = [next[target], next[index]]
    setExpandedBlocks((prev) => {
      const out = { ...prev }
      const a = prev[index]
      const b = prev[target]
      out[index] = b ?? true
      out[target] = a ?? true
      return out
    })
    onChange?.(normalizeOrders(next))
  }

  const toggleExpand = (index: number) => {
    setExpandedBlocks((prev) => ({ ...prev, [index]: !prev[index] }))
  }

  const allExpanded = useMemo(
    () => blocks.length > 0 && blocks.every((_, i) => expandedBlocks[i] !== false),
    [blocks, expandedBlocks],
  )

  const toggleAll = () => {
    const target = !allExpanded
    setExpandedBlocks(
      blocks.reduce<Record<number, boolean>>((acc, _, i) => {
        acc[i] = target
        return acc
      }, {}),
    )
  }

  return (
    <Box>
      {/* Block list */}
      {blocks.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 6,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            borderStyle: 'dashed',
            borderWidth: 1,
            borderRadius: 2,
            bgcolor: 'action.hover',
            mb: 2,
          }}
        >
          <AddBoxIcon
            sx={{ fontSize: 56, color: 'text.disabled', opacity: 0.4, mb: 1 }}
          />
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="text.secondary"
            sx={{ opacity: 0.8, mb: 0.5 }}
          >
            ยังไม่มี Content Block
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
            เลือกประเภท Block ที่ต้องการด้านล่าง เพื่อเริ่มสร้าง Salepage ของคุณ
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5,
            }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              ทั้งหมด {blocks.length} Blocks
            </Typography>
            <Tooltip title={allExpanded ? 'ย่อทั้งหมด' : 'ขยายทั้งหมด'}>
              <IconButton size="small" onClick={toggleAll} disabled={disabled}>
                {allExpanded ? (
                  <UnfoldLessIcon fontSize="small" />
                ) : (
                  <UnfoldMoreIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={blockIds}
              strategy={verticalListSortingStrategy}
            >
              {blocks.map((block, index) => (
                <SortableBlockItem
                  key={blockIds[index]}
                  id={blockIds[index]}
                  index={index}
                  block={block}
                  blocksLength={blocks.length}
                  expanded={expandedBlocks[index] ?? true}
                  disabled={disabled}
                  onToggleExpand={toggleExpand}
                  onMoveBlock={moveBlock}
                  onCloneBlock={cloneBlock}
                  onRemoveBlock={removeBlock}
                  onUpdateBlock={updateBlock}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Box>
      )}

      {/* Block picker */}
      <Paper
        variant="outlined"
        sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}
      >
        <Typography
          variant="caption"
          fontWeight={600}
          color="text.secondary"
          sx={{ display: 'block', mb: 1.5 }}
        >
          + เลือกประเภท Block ที่ต้องการเพิ่ม
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: 1,
          }}
        >
          {BLOCK_TYPES.map((type) => {
            const meta = BLOCK_TYPE_META[type]
            return (
              <Button
                key={type}
                variant="outlined"
                disabled={disabled}
                onClick={() => addBlock(type)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.75,
                  py: 2,
                  px: 1.5,
                  textAlign: 'center',
                  borderColor: 'divider',
                  color: 'text.secondary',
                  '&:hover': {
                    borderColor: meta.color,
                    color: meta.color,
                    bgcolor: `${meta.color}0D`,
                  },
                }}
              >
                <Box sx={{ color: meta.color, '& .MuiSvgIcon-root': { fontSize: 28 } }}>
                  {BLOCK_ICONS[type]}
                </Box>
                <Typography variant="body2" fontWeight={600}>
                  {meta.label}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {meta.desc}
                </Typography>
              </Button>
            )
          })}
        </Box>
      </Paper>
    </Box>
  )
}

export default BlockEditor
